import React, { useEffect, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { Form, Col, Row, Accordion } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import Select from 'react-select';
import { useSelector } from 'react-redux';
// slices
import { companySelector } from 'slices/company';
// components
import CustomInput from 'components/backoffice/customInput';
import FooterModal from 'components/backoffice/modals/FooterModal';
import CkEditorComponent from 'components/backoffice/inputEditor/InputEditorComponent';
import CustomCheckbox from 'components/backoffice/customCheckbox';

const FaqForm = ({ onSubmit, faq }) => {
  const { t } = useTranslation(['backoffice']);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const { languages, faqsTotals } = useSelector(companySelector);
  const [choices, setChoices] = useState([{ value: 0, label: 0 }]);
  const [openAccordion, setOpenAccordion] = useState({});
  const [editorErrors, setEditorErrors] = useState({});

  const {
    formState: { errors },
    setValue,
    control,
    handleSubmit,
  } = useForm();

  useEffect(() => {
    if (Object.keys(faq).length > 0) {
      setValue('id', faq.id);
      languages.map((lan) => {
        const customQ = `question_${lan.code_iso2}`;
        const customA = `answer_${lan.code_iso2}`;
        setValue(customQ, faq[customQ]);
        setValue(customA, faq[customA]);
      });
      setValue('order', { value: faq.order, label: faq.order });
      setValue('active', faq.active);
    }
  }, [faq]);

  const submit = (values) => {
    onSubmit(values);
    setChoices([]);
  };

  const handleCancel = () => {
    setChoices([]);
    navigate(-1);
  };

  useEffect(() => {
    if (faqsTotals.length > 0)
      if (Object.keys(faq).length > 0) {
        setChoices([...faqsTotals.map((val) => ({ value: val - 1, label: val - 1 }))]);
      } else {
        setChoices((prev) => [...prev, ...faqsTotals.map((val) => ({ value: val, label: val }))]);
      }
  }, [faqsTotals, faq]);

  useEffect(() => {
    if (Object.keys(errors).length > 0) {
      const errorLanguageCodes = Object.keys(errors).map((key) => key.split('_')[1]);
      const openAccordionState = errorLanguageCodes.reduce((acc, langCode) => {
        acc[langCode] = true;
        return acc;
      }, {});
      setOpenAccordion(openAccordionState);
    }
  }, [errors]);

  const handleAccordionToggle = (languageCode) => {
    setOpenAccordion((prevOpen) => ({
      ...prevOpen,
      [languageCode]: !prevOpen[languageCode],
    }));
  };

  return (
    <div className='backoffice-accordion mt-5'>
      <Form onSubmit={handleSubmit(submit)}>
        {Object.keys(faq).length > 0 && (
          <Row hidden={true}>
            <Col md={12}>
              <Controller
                name="id"
                control={control}
                defaultValue={0}
                render={({ field: { onChange, value } }) => (
                  <CustomInput
                    value={value}
                    type="number"
                    onChange={(e) => onChange(e)}
                  />
                )}
              />
            </Col>
          </Row>
        )}
        <Row className='mt-4 mb-2'>
          <Col sm={12} md={12} lg={6} className="mb-md-2 mb-lg-0 select">
            <Form.Label className="text-light">{t('faqs.order')}</Form.Label>
            <Controller
              name="order"
              control={control}
              rules={{
                required: {
                  value: true,
                  message: t('camporequerido'),
                },
              }}
              defaultValue={''}
              render={({ field }) => (
                <Select
                  noOptionsMessage={() => t('promoCodes.noOptions')}
                  {...field}
                  options={choices}
                  placeholder={t('select')}
                  className="select-companies"
                  classNamePrefix="select-companies"
                />
              )}
            />
            {errors.order && <small className="text-danger ">{errors.order.message}</small>}
          </Col>
          <Col sm={12} md={12} lg={6} className="mt-4 mt-sm-0 mb-md-2 mb-lg-0">
            <Controller
              name="active"
              control={control}
              defaultValue={false}
              render={({ field }) => (
                <CustomCheckbox
                  label={t('activo')}
                  checked={field.value}
                  onChange={(e) => field.onChange(e.target.checked)}
                />
              )}
            />
          </Col>
        </Row>
        <div className="card-faqs-container">
          <Row className="mx-1 w-100">
            {languages.map((lan) => {
              const customQuestion = `question_${lan.code_iso2}`;
              const customAnswer = `answer_${lan.code_iso2}`;
              const isOpen = openAccordion[lan.code_iso2];
              return (
                <Accordion
                  key={lan.nombre}
                  className="mt-3 card-faqs"
                  activeKey={isOpen ? '0' : null}
                >
                  <Accordion.Item eventKey="0" className="bg-transparent border-0">
                    <Accordion.Header
                      className="text-light card-faqs-header mt-0"
                      onClick={() => handleAccordionToggle(lan.code_iso2)}
                    >
                      {lan.nombre}
                    </Accordion.Header>
                    <Accordion.Body>
                      <div className="card-faqs-line mb-2" />
                      <div className="d-flex flex-row">
                        <div className="card-faqs-item">
                          <Controller
                            name={customQuestion}
                            control={control}
                            rules={{
                              required: {
                                value: true,
                                message: t('camporequerido'),
                              },
                            }}
                            defaultValue=""
                            render={({ field: { onChange, value } }) => (
                              <CustomInput
                                value={value}
                                type="text"
                                label={t('faqs.question')}
                                placeholder={t('faqs.question')}
                                onChange={onChange}
                              />
                            )}
                          />
                          {errors[customQuestion] && (
                            <small className="text-danger">
                              {errors[customQuestion].message}
                            </small>
                          )}
                        </div>
                        <div className="card-faqs-item">
                          <Form.Label className="text-light">
                            {t('faqs.answer')}
                          </Form.Label>
                          {isOpen && (
                            <Controller
                              name={customAnswer}
                              control={control}
                              rules={{
                                required: {
                                  value: true,
                                  message: t('camporequerido'),
                                },
                              }}
                              defaultValue=""
                              render={({ field: { value, onChange } }) => (
                                <CkEditorComponent
                                  value={value}
                                  onChange={onChange}
                                  placeholder={t('faqs.answer')}
                                />
                              )}
                            />
                          )}
                          {errors[customAnswer] && (
                            <small className="text-danger">
                              {errors[customAnswer].message}
                            </small>
                          )}
                        </div>
                      </div>
                    </Accordion.Body>
                  </Accordion.Item>
                </Accordion>
              );
            })}
          </Row>
        </div>
      </Form>
      <div className="w-100 mt-5 d-flex justify-content-end gap-3">
        <FooterModal
          onCancel={handleCancel}
          onSave={handleSubmit(submit)}
          loading={loading}
        />
      </div>
    </div>
  );
};

FaqForm.propTypes = {
  onSubmitForm: PropTypes.func.isRequired,
  faq: PropTypes.object,
};

export default FaqForm;
